<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

const props = defineProps({
    // Bildirimi versiyonlayın; metinde değişiklik olursa "v2" yaparsınız, eski kabul geçersiz olur
    storageKey: { type: String, default: 'cookie_consent_v1' },
    // Kaç gün sonra tekrar gösterilsin? (0 veya null => asla tekrar gösterme)
    expiryDays: { type: Number, default: 0 },
    // Politika sayfa linki
    policyUrl: { type: String, default: '/cookie-policy' },
});

const open = ref(false);

function hasExpired(saved) {
    if (!saved) return true;
    try {
        const data = JSON.parse(saved);
        if (!data?.ts) return true;
        if (!props.expiryDays || props.expiryDays <= 0) return false;
        const now = Date.now();
        const diff = now - data.ts;
        const max = props.expiryDays * 24 * 60 * 60 * 1000;
        return diff > max;
    } catch {
        return true;
    }
}

function accept() {
    localStorage.setItem(props.storageKey, JSON.stringify({ ts: Date.now(), locale: locale.value }));
    open.value = false;
}

onMounted(() => {
    const saved = localStorage.getItem(props.storageKey);
    open.value = !saved || hasExpired(saved);
});
</script>

<template>
    <Teleport to="body">
        <transition name="slide-up">
            <div v-if="open" class="fixed inset-x-0 bottom-0 z-[60] mx-auto w-full max-w-7xl px-3 pb-3 sm:px-0" role="region" aria-label="Cookie consent">
                <div class="rounded-2xl border border-gray-200 bg-white/95 shadow-2xl backdrop-blur dark:border-gray-700 dark:bg-gray-900/90">
                    <div class="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                        <div class="flex items-start gap-3">
                            <div class="mt-0.5 hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:ring-blue-800/40 sm:flex">🍪</div>
                            <p class="text-sm text-gray-700 dark:text-gray-200">
                                {{ t('app.cookieNotice') || 'Size daha iyi bir deneyim sunmak için çerezler kullanıyoruz.' }}
                                <a :href="policyUrl" class="font-semibold text-blue-600 underline-offset-2 hover:underline dark:text-blue-400" target="_self" rel="noopener">
                                    {{ t('app.cookiePolicy') || 'Çerez Politikası' }}
                                </a>
                            </p>
                        </div>

                        <div class="flex items-center gap-2">
                            <button type="button" class="inline-flex items-center rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200" @click="open = false">
                                {{ t('app.later') || 'Daha Sonra' }}
                            </button>
                            <button type="button" class="inline-flex items-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-xs font-bold text-white shadow hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" @click="accept">
                                {{ t('app.accept') || 'Kabul Ediyorum' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.35s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
    transform: translateY(12px);
    opacity: 0;
}
</style>
